import { queue } from '~/components/ui/toast-queue'

/**
 * Lendio-owned toast service (replaces PrimeVue's `useToast` from `primevue/usetoast`).
 *
 * The public `add()` signature intentionally matches PrimeVue's so existing callers only
 * change their import line. The queue is a module-level singleton rendered by the single
 * `<UiToastHost>` mounted in `app.vue`, so toasts survive layout switches and calls made
 * before the host mounts.
 *
 * Import explicitly (`import { useToast } from '~/composables/useToast'`) rather than
 * relying on Nuxt auto-import: vitest does not run Nuxt's auto-imports, and explicit
 * imports are what `vi.mock('~/composables/useToast')` intercepts.
 */

export type ToastSeverity = 'success' | 'error' | 'info' | 'warn'

export interface ToastMessage {
  /** PrimeVue naming kept on purpose; mapped to a Web Awesome `variant` by the host. */
  severity?: ToastSeverity
  summary?: string
  detail?: string
  /** Auto-dismiss delay in ms. `undefined` or `0` keeps the toast until closed (PrimeVue semantics). */
  life?: number
  /** Show a close button. Defaults to true. */
  closable?: boolean
}

export interface ToastEntry extends ToastMessage {
  id: number
  severity: ToastSeverity
  closable: boolean
}

let nextId = 0

export function useToast() {
  return {
    add(message: ToastMessage): number {
      // Defaults are applied AFTER the spread: a caller passing `severity: undefined`
      // (e.g. `cond ? 'error' : undefined`) must still land on 'info', not override it.
      const entry: ToastEntry = {
        ...message,
        id: ++nextId,
        severity: message.severity ?? 'info',
        closable: message.closable ?? true,
        life: Math.max(0, message.life ?? 0),
      }
      queue.push(entry)
      return entry.id
    },
    remove(id: number): void {
      const index = queue.findIndex((toast) => toast.id === id)
      if (index !== -1) {
        queue.splice(index, 1)
      }
    },
    removeAll(): void {
      queue.splice(0)
    },
  }
}
